import { BUILD_GIT_SHA, BUILD_ID, BUILD_TIME } from './buildInfo';

export interface DeployedVersion {
  buildId: string;
  builtAt: string;
  gitSha: string;
}

export interface PendingReloadInfo {
  fromBuildId: string;
  toBuildId: string;
  reason: string;
  detectedAt: string;
}

export const VERSION_URL = '/version.json';
export const PENDING_RELOAD_STORAGE_KEY = 'workbench.pendingBuildReload';

export function currentBuildInfo(): DeployedVersion {
  return {
    buildId: BUILD_ID,
    builtAt: BUILD_TIME,
    gitSha: BUILD_GIT_SHA,
  };
}

export async function fetchDeployedVersion(fetchImpl: typeof fetch = fetch): Promise<DeployedVersion | null> {
  const response = await fetchImpl(`${VERSION_URL}?ts=${Date.now()}`, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) return null;
  const data = await response.json();
  if (!isDeployedVersion(data)) return null;
  return data;
}

export function shouldReloadForVersion(
  current: DeployedVersion,
  next: DeployedVersion | null,
): boolean {
  return !!next && next.buildId !== current.buildId;
}

export function isChunkLoadFailure(value: unknown): boolean {
  if (value == null) return false;

  if (typeof value === 'string') {
    return /chunkloaderror|loading chunk [\w-]+ failed|failed to fetch dynamically imported module/i
      .test(value);
  }

  if (value instanceof Error) {
    return isChunkLoadFailure(`${value.name} ${value.message}`);
  }

  if (typeof Event !== 'undefined' && value instanceof Event) {
    const target = value.target as { tagName?: string; src?: string; href?: string } | null;
    if (target) {
      const tag = (target.tagName || '').toUpperCase();
      if ((tag === 'SCRIPT' || tag === 'LINK')
        && /\/static\/.*\.(js|css)(\?|$)/i.test(target.src || target.href || '')) {
        return true;
      }
    }
    const eventLike = value as Event & { message?: unknown; error?: unknown };
    return isChunkLoadFailure(eventLike.error) || isChunkLoadFailure(eventLike.message);
  }

  if (typeof value === 'object') {
    const errorLike = value as { message?: unknown; name?: unknown; reason?: unknown };
    return isChunkLoadFailure(errorLike.reason)
      || isChunkLoadFailure(errorLike.message)
      || isChunkLoadFailure(errorLike.name);
  }

  return false;
}

export function readPendingReloadInfo(storage: Storage | null): PendingReloadInfo | null {
  if (!storage) return null;
  const raw = storage.getItem(PENDING_RELOAD_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed
      && typeof parsed.fromBuildId === 'string'
      && typeof parsed.toBuildId === 'string'
      && typeof parsed.reason === 'string'
      && typeof parsed.detectedAt === 'string'
    ) {
      return parsed as PendingReloadInfo;
    }
  } catch {
    // ignore malformed storage
  }
  return null;
}

export function writePendingReloadInfo(storage: Storage | null, info: PendingReloadInfo): void {
  try {
    storage?.setItem(PENDING_RELOAD_STORAGE_KEY, JSON.stringify(info));
  } catch {
    // QuotaExceededError or SecurityError in private browsing — non-critical.
  }
}

export function clearPendingReloadInfo(storage: Storage | null): void {
  storage?.removeItem(PENDING_RELOAD_STORAGE_KEY);
}

function isDeployedVersion(value: unknown): value is DeployedVersion {
  if (!value || typeof value !== 'object') return false;
  const data = value as Record<string, unknown>;
  return typeof data.buildId === 'string'
    && typeof data.builtAt === 'string'
    && typeof data.gitSha === 'string';
}
